import { Link } from "react-router-dom";
import { NavLink, Tooltip } from "@mantine/core";
import type { NavItem } from "../router/nav";

interface SidebarNavLinkProps {
  item: NavItem;
  /** Caller-computed (portals differ: some match exact, some `startsWith`). */
  active: boolean;
  /** Render as a centered icon with a hover-tooltip label (the mini rail). */
  mini: boolean;
  onClick?: () => void;
}

/**
 * One sidebar entry, shared by every portal shell. Expanded shows icon + label;
 * on the mini rail the label drops to a right-side hover tooltip and the icon
 * centers, so destinations stay identifiable without text.
 */
export function SidebarNavLink({
  item,
  active,
  mini,
  onClick,
}: SidebarNavLinkProps) {
  if (mini) {
    return (
      <Tooltip label={item.label} position="right" withArrow>
        <NavLink
          component={Link}
          to={item.path}
          leftSection={<item.icon size={20} />}
          active={active}
          onClick={onClick}
          mb="xs"
          aria-label={item.label}
          styles={{
            root: { justifyContent: "center" },
            section: { marginInlineEnd: 0 },
            // Drop the empty label wrapper so the lone icon truly centers.
            body: { display: "none" },
          }}
        />
      </Tooltip>
    );
  }
  return (
    <NavLink
      component={Link}
      to={item.path}
      label={item.label}
      leftSection={<item.icon size={20} />}
      active={active}
      onClick={onClick}
      mb="xs"
      // 41px by default — under the 44px touch minimum the iPad point-of-care
      // shell is held to. Unlike the header icons (which keep their drawn size
      // and grow only their hit region), a sidebar row has the vertical room:
      // an 8px margin leaves 5px of clearance at 44px, so no two rows overlap.
      styles={{ root: { minHeight: 44 } }}
    />
  );
}
